// discover-filters.jsx — Advanced filter sheet for Discover screen
// ─────────────────────────────────────────────────────────────────────────
//  Surfaces every Supabase filterable field as a multi-select chip group:
//    provider, specialty, language, teaching mode, event type, audience,
//    enrolment status, plus quick deadline / points / fee ranges.
//
//  Design choices:
//    • Options are computed from the *currently loaded* COURSES set, not a
//      hard-coded enum. So if Supabase ships a new specialty next week,
//      it shows up without a code change. Each option carries its course
//      count — users can see which filters have results.
//    • Selections are multi-select OR within a facet, AND across facets.
//      Matches how nurses actually browse ("Cantonese OR English, but
//      definitely Cardiac AND must be Open").
//    • The sheet header has a live "X courses" count that updates as you
//      tick — so users know whether they're cutting too aggressively
//      before they hit Apply.
//    • Sticky footer with Reset (clears just *this* sheet) and the live
//      Apply button.
//
//  Public API:
//    DiscoverFilters.facets(courses)                  → derived facets
//    DiscoverFilters.applyFilters(courses, filters)   → filtered list
//    DiscoverFilters.countActive(filters)             → integer
//    DiscoverFilters.emptyFilters()                   → blank filter state
//    <DiscoverFilterSheet filters onChange onClose lang courses />
// ─────────────────────────────────────────────────────────────────────────

// Hooks are called as `React.useX(...)` member calls so react-hooks/
// rules-of-hooks can see them (it only recognises `/^use[A-Z]/` and
// `React.useX`). Do not reintroduce destructured short aliases.

// ── Facet config ──────────────────────────────────────────────────────────
// Each facet maps to an array field (multi-valued, like specialty_area_en)
// or a scalar (like language_en). The shape decides how applyFilters reads it.
// ── Canonical values, because a nurse does not know which string the provider
// typed ────────────────────────────────────────────────────────────────────
//
// ‼️ THE FACET USED TO KEY ON THE RAW COLUMN, SO ONE CONCEPT BECAME N CHIPS.
// Measured 2026-08-16 on the live catalogue: language carried 10 distinct
// values where six of them (Cantonese and English / Cantonese supplemented
// with English / Cantonese with English lecture notes / 英文簡報 / 廣東話講授 /
// Cantonese/English / English slides; Cantonese instruction) all mean the same
// thing to someone choosing a course; event type carried 29, including `Course`
// and `course` as separate options.
//
// Two rules, from the earlier design review:
//   • FILTER IS A COARSE CLASSIFICATION; DETAIL KEEPS THE PROVIDER'S WORDING.
//     「廣東話授課,英文講義」 is a real distinction and belongs on the course page.
//     It is not a fifth thing to choose between in a chip list.
//   • Canonicalisation is RULE-BASED, not a lookup table of today's strings —
//     a table is wrong the week a provider types a new variant, and the variant
//     lands silently in 其他 where nobody sees it.
const DF_LANG_CANON = [
  { value: 'cantonese',  label: { en: 'Cantonese',           zh: '廣東話' } },
  { value: 'english',    label: { en: 'English',             zh: '英語' } },
  { value: 'mandarin',   label: { en: 'Mandarin',            zh: '普通話' } },
  { value: 'cant_eng',   label: { en: 'Cantonese + English', zh: '廣東話＋英語' } },
  { value: 'eng_man',    label: { en: 'English + Mandarin',  zh: '英語＋普通話' } },
  { value: 'other',      label: { en: 'Other',               zh: '其他' } },
];
function dfCanonLanguage(raw) {
  const s = String(raw || '').toLowerCase();
  if (!s.trim()) return null;                       // no value ≠ a bucket
  const cant = /cantonese|廣東話|粵語/.test(s);
  const eng  = /english|英語|英文/.test(s);
  const man  = /mandarin|putonghua|普通話|國語/.test(s);
  if (cant && eng) return 'cant_eng';
  if (eng && man)  return 'eng_man';
  if (cant) return 'cantonese';
  if (eng)  return 'english';
  if (man)  return 'mandarin';
  return 'other';
}
// Event type stays close to the provider's own word — the shapes genuinely
// differ to someone choosing — but case and spacing are not a distinction.
function dfCanonEventType(raw) {
  const s = String(raw || '').trim();
  if (!s) return null;
  return s.replace(/\s+/g, ' ').toLowerCase();
}
function dfEventTypeLabel(canonValue, seenRaw) {
  const raw = seenRaw || canonValue;
  return { en: raw, zh: dfZh(raw) };
}

// ‼️ ONE ACCESSOR PER FACET, READ BY BOTH THE COUNTER AND THE FILTER. They used
// to walk the row separately — the counter over `c.raw[key]`, the filter over
// `c.raw[key]` again with its own null handling — which is how a canonical
// value can be counted under one rule and matched under another. `read` returns
// the canonical values for a course; everything downstream is set membership.
// ═══════════════════════════════════════════════════════════════════════════
// PROVIDER — keyed on IDENTITY, not on an import artefact
// ═══════════════════════════════════════════════════════════════════════════
// ‼️ THIS FACET USED TO READ `provider_code`, AND THEREFORE ASKED THE WRONG
// QUESTION. It asked "does this course carry a legacy provider code?" instead of
// "which organisation is this?". provider_code is produced only by the OFFICIAL
// import path — the community submission form never collects one — so on
// 2026-08-20, 57 of the 271 courses in the app pool (21%, ALL of them
// source='community') returned null here and were unreachable through the
// filter, along with the 16 real organisations behind them. The identity layer
// already knew the answer: 55 of those 57 carried a resolved provider_entity_id.
//
// ‼️ NEVER GO BACK TO A NAME OR A CODE. Grouping by the raw provider string would
// split one organisation across its spellings — "Tung Wah College" and "Tung Wah
// College School of Nursing" are 4 and 8 courses of the SAME entity (12), and
// §53 refused name-derived identity for exactly this reason.
//
// ‼️ EVERY COURSE LANDS IN EXACTLY ONE BUCKET, INCLUDING THE UNRESOLVED ONES.
// `read` never returns null: an unresolved identity gets the sentinel below and
// a 「待確認主辦機構」 chip. That is not a fabricated provider — it is the course's
// identity STATE, shown as itself, the same rule as `fee null ≠ free` and
// `unknown enrolment ≠ closed`. It also makes the arithmetic closed: the chip
// counts sum to the pool size, so "the chips say 91 but the list says 97" can no
// longer happen. Asserted by npm run test:discover-filters.
const DF_PROVIDER_UNRESOLVED = '__provider_unresolved__';

// Last-resort chip text, used only until the dictionary arrives (first launch,
// offline with an empty cache). ‼️ min(), NOT first-seen: a label that depends on
// array order is the nondeterminism this whole facet was re-keyed to remove. It
// is display-only and never written back anywhere.
let dfProviderFallback = new Map();
function dfBuildProviderFallback(list) {
  const m = new Map();
  (list || []).forEach(c => {
    const id = c?.raw?.provider_entity_id;
    if (!id) return;
    const cur = m.get(String(id)) || { en: null, zh: null };
    const en = c?.raw?.provider_full_en, zh = c?.raw?.provider_full_zh;
    if (en && (!cur.en || String(en) < cur.en)) cur.en = String(en);
    if (zh && (!cur.zh || String(zh) < cur.zh)) cur.zh = String(zh);
    m.set(String(id), cur);
  });
  return m;
}

function dfProviderBucket(c) {
  const id = c?.raw?.provider_entity_id;
  return (id && String(id).trim()) ? String(id) : DF_PROVIDER_UNRESOLVED;
}

function dfProviderLabel(value) {
  if (value === DF_PROVIDER_UNRESOLVED) {
    return { en: 'Provider not yet confirmed', zh: '待確認主辦機構' };
  }
  const canonical = window.PROVIDER_LABELS?.get?.(value);
  if (canonical && (canonical.en || canonical.zh)) {
    return { en: canonical.en || canonical.zh, zh: canonical.zh || canonical.en };
  }
  const fb = dfProviderFallback.get(value);
  if (fb && (fb.en || fb.zh)) return { en: fb.en || fb.zh, zh: fb.zh || fb.en };
  return { en: value, zh: value };
}

const DF_FACETS = [
  { key: 'provider_entity_id', label: { en: 'Provider',  zh: '主辦機構' },
    read: c => [dfProviderBucket(c)],
    labelOf: value => dfProviderLabel(value) },
  { key: 'specialty_area_en', label: { en: 'Specialty', zh: '專科' },
    read: c => (Array.isArray(c?.raw?.specialty_area_en) ? c.raw.specialty_area_en : []) },
  { key: 'event_type_en',     label: { en: 'Event type', zh: '類型' },
    read: c => [dfCanonEventType(c?.raw?.event_type_en)],
    labelOf: (v, raw) => dfEventTypeLabel(v, raw), rawOf: c => c?.raw?.event_type_en },
  { key: 'language_en',       label: { en: 'Language', zh: '語言' },
    read: c => [dfCanonLanguage(c?.raw?.language_en)], options: DF_LANG_CANON,
    // Coverage on the pool she is browsing, not on the whole table: 85% of
    // unfinished courses publish no language at all (2026-08-16), so picking
    // 廣東話 silently drops five sixths of the catalogue.
    coverage: true },
  { key: 'deliveryMode',      label: { en: 'Delivery', zh: '授課方式' },
    // ‼️ deriveDeliveryMode, NOT teaching_mode_en. The raw column is null on 64%
    // of unfinished courses and splits Online/Zoom/Online (Zoom) and
    // Face-to-face/In-person; the derivation resolves 81% and is the same fact
    // the quick 網上 chip uses, so the two can no longer disagree 5×.
    read: c => [window.deriveDeliveryMode?.(c?.raw || {})?.mode].filter(m => m && m !== 'unknown'),
    options: [
      { value: 'in_person', label: { en: 'In person', zh: '面授' } },
      { value: 'online',    label: { en: 'Online',    zh: '網上' } },
      { value: 'hybrid',    label: { en: 'Hybrid',    zh: '混合' } },
    ] },
  { key: 'target_audience_en', label: { en: 'For', zh: '對象' },
    read: c => (Array.isArray(c?.raw?.target_audience_en) ? c.raw.target_audience_en : []) },
  {
    key: 'registrationStatus', label: { en: 'Enrolment', zh: '報名狀態' },
    // ‼️ 'unknown' IS ITS OWN ANSWER, NOT A QUIET 「可報名」. This used to fold
    // open + waitlist + unknown into one 可報名 chip. The fold was justified when
    // written — unknown was then the largest bucket, and hiding it emptied the
    // list — but on 2026-08-16 open is 67 and unknown 31, so the premise has
    // expired while the wording stayed.
    //
    // Calling "the provider published nothing" 可報名 is the same error the fee
    // contract already refuses: `fee null ≠ free`. Absence of evidence that
    // registration is closed is not evidence that it is open, and 「可報名」 is a
    // promise she acts on by budgeting and booking.
    //
    // Waitlist still folds into 可報名: joining a queue is an action she can
    // take today, which is what the chip claims.
    //
    // ‼️ NAMED 未公佈, NOT 未確認 — 待確認 is already the CNE-points state below,
    // and two 「確認」 states in one sheet is how the fold got excused the first
    // time. Matches 未公布時間 in the shift facet.
    get: c => {
      const s = window.deriveRegistrationStatus?.(c) || 'unknown';
      if (s === 'waitlist') return 'open';
      return s;
    },
    read: c => {
      const s = window.deriveRegistrationStatus?.(c) || 'unknown';
      return [s === 'waitlist' ? 'open' : s];
    },
    options: [
      { value: 'open', label: { en: 'Available', zh: '可報名' } },
      { value: 'unknown', label: { en: 'Not published', zh: '未公佈' } },
      { value: 'full', label: { en: 'Full', zh: '已滿' } },
      { value: 'closed', label: { en: 'Closed', zh: '已截止' } },
    ],
  },
  {
    key: 'cneStatusNorm', label: { en: 'CNE points status', zh: 'CNE 分狀態' },
    get: c => window.deriveCneStatusNorm?.(c) || 'unknown',
    read: c => [window.deriveCneStatusNorm?.(c) || 'unknown'],
    options: [
      { value: 'confirmed', label: { en: 'Confirmed', zh: '已確認' } },
      { value: 'pending', label: { en: 'Pending', zh: '待確認' } },
      { value: 'unknown', label: { en: 'No data', zh: '未有資料' } },
    ],
  },
  {
    // Shift-friendliness — "can I actually attend this around my roster?".
    // Single-valued so it behaves like the other derived facets; a course that
    // is both evening AND weekend reports 'weekend' (the stronger signal for a
    // shift worker, since it clears a whole day rather than an evening).
    //
    // 'unknown' is a REAL option, not an oversight: only ~52% of active courses
    // publish a start time. Without it, an empty result would read as "there
    // are no evening courses" when the truth is "we don't know for 48% of them".
    key: 'shiftTag', label: { en: 'Fits my shift', zh: '配合更表' },
    get: (c) => {
      const s = c.shiftTag;
      if (!s) return 'unknown';
      if (s.weekend === true) return 'weekend';
      if (s.evening === true) return 'evening';
      if (s.evening === false || s.weekend === false) return 'daytime';
      return 'unknown';
    },
    read: (c) => {
      const t = c?.shiftTag;
      if (!t) return ['unknown'];
      if (t.weekend === true) return ['weekend'];
      if (t.evening === true) return ['evening'];
      if (t.evening === false || t.weekend === false) return ['daytime'];
      return ['unknown'];
    },
    options: [
      { value: 'evening', label: { en: 'Evening start', zh: '夜間開課' } },
      { value: 'weekend', label: { en: 'Weekend', zh: '週末' } },
      { value: 'daytime', label: { en: 'Weekday daytime', zh: '平日日間' } },
      { value: 'unknown', label: { en: 'Time not published', zh: '未公布時間' } },
    ],
  },
];

// Synonym mapping for translating raw EN values to ZH for display.
// Falls back to the EN value if we don't have a translation.
const DF_ZH_SYNONYMS = {
  // teaching modes
  'Online':                          '網上',
  'Face-to-face':                    '面授',
  'Zoom':                            'Zoom',
  'Online (Zoom)':                   '網上（Zoom）',
  'Self-study':                      '自學',
  'Mixed (Zoom + Face-to-face)':     'Zoom + 面授',
  // languages
  'Cantonese':                       '廣東話',
  'English':                         '英語',
  'Mandarin':                        '普通話',
  'Bilingual (Cantonese/English)':   '雙語（廣東話／英語）',
  'Bilingual (English/Mandarin)':    '雙語（英語／普通話）',
  'English PowerPoint, Cantonese verbal': '英文簡報 / 廣東話講授',
  // enrolment status
  'Open':                            '可報名',
  'Full':                            '已滿',
  'Closed':                          '已截止',
  'Enrollment ended':                '報名已結束',
  'Opening Soon':                    '即將開放',
  // Both are documented catalogue values (see data.js's schema note) that had no
  // translation, so a zh nurse read raw English in the filter chip. Worse for
  // 'Not Yet Open', which she could scan as the 'Open' she was looking for.
  'Not full':                        '尚有名額',
  'Not Yet Open':                    '尚未開放',
  // specialty (most common)
  'Critical Care':                   '危重病護理',
  'Cardiac':                         '心臟科',
  'Emergency':                       '急症',
  'Oncology':                        '腫瘤科',
  'Paediatrics':                     '兒科',
  'Mental Health':                   '精神健康',
  'Orthopaedics':                    '骨科',
  'Renal':                           '腎科',
  'Respiratory':                     '呼吸科',
  'Obstetrics':                      '婦產科',
  'Palliative':                      '紓緩護理',
  'Infection Control':               '感染控制',
  'Wound Care':                      '傷口護理',
  'Endoscopy':                       '內窺鏡',
  'Diabetes':                        '糖尿病',
  'Neurology':                       '神經科',
  'Urology':                         '泌尿科',
  'Gerontology':                     '老人科',
  'Perioperative':                   '手術室護理',
  'Community':                       '社區',
  'Management':                      '管理',
  'Research':                        '研究',
  'Education':                       '教育',
  'General':                         '一般',
  // audience
  'RN':                              '註冊護士',
  'EN':                              '登記護士',
  'Midwife':                         '助產士',
  'Student Nurse':                   '學生護士',
  'APN':                             '高級執業護士',
  'Advanced Practice Nurses':        '高級執業護士',
  'Allied Health':                   '醫療輔助人員',
  'Doctor':                          '醫生',
  'Healthcare Professional':         '醫護人員',
  'Healthcare Professionals':        '醫護人員',
  'Nurses':                          '護士',
  'Specialist Nurse':                '專科護士',
  'Nursing managers and nurses in leadership roles': '護理管理及領導職位',
  'Nursing Manager':                 '護理管理',
  'General public and caregivers':   '公眾及照顧者',
  'Nurses & Allied Health':          '護士及醫療輔助人員',
};

function dfZh(en) {
  return DF_ZH_SYNONYMS[en] || en;
}

// "Today" as a LOCAL calendar date (YYYY-MM-DD).
//
// ‼️ NEVER `new Date().toISOString().slice(0, 10)` here. That is UTC, so
// between 00:00 and 08:00 Hong Kong time it returns yesterday and the ≤N-days
// window slides a day late — courses whose deadline expired yesterday score
// diff === 0 and are presented as "closing today". Delegates to data.js's
// todayIso (the single source); the inline fallback keeps this file standalone.
function dfToday() {
  if (typeof window !== 'undefined' && typeof window.todayIso === 'function') return window.todayIso();
  const now = new Date();
  const pad = v => String(v).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

// Build a fresh empty filter state. Each facet gets an array; ranges get null.
// restrictedMode is a tri-state pool control:
//   'relevant' (default) — restricted courses the user may qualify for
//                          (their hospital + all membership/unknown)
//   'all'                — every restricted course, incl. ineligible
//   'none'               — hide all restricted courses
function dfEmpty() {
  const out = {
    search: '', cost: null, mode: null, deadlineDays: null, points: null,
    // priceMax EXTENDS `cost` rather than replacing it: `cost` is deep-linked
    // from Discover's quick chips and asserted by scripts/test-discover.mjs, so
    // swapping it for bands would break both for no user-visible gain. The two
    // compose — 免費 is cost:'free', the bands narrow the paid range.
    priceMax: null,
    restrictedMode: 'relevant',
  };
  DF_FACETS.forEach(f => { out[f.key] = []; });
  return out;
}

// Compute facet → option list with counts, from the live courses set.
// Each option = { value: 'AHKNS', count: 23, label: { en, zh } }
// Facet options with counts, computed on the pool she is actually browsing.
//
// ‼️ THREE BUGS LIVED HERE AT ONCE, AND ALL THREE MADE THE NUMBER A LIE.
//   1. The universe included ARCHIVED courses. 「網上 42」 next to a list holding
//      8 of them (2026-08-16). A facet count is a preview of a result set, and
//      this repo already states the rule for the Free-CNE card: the predicate
//      must match the destination list exactly, because the count is a promise.
//   2. It ignored every other selection, so counts never narrowed as she
//      filtered — 語言 still read 130 with 免費 + 網上 already on.
//   3. Counter and filter walked the row separately, so a canonical value could
//      be counted under one rule and matched under another. Both now go through
//      the facet's single `read`.
//
// The count is MARGINAL: every OTHER facet applied, this one not. That is the
// standard faceted-search meaning ("how many of this kind are there, given what
// I have already chosen") and it keeps one formula for selected and unselected
// options alike. It is deliberately NOT "what the result set becomes if I tap
// this" — under OR-within-a-facet that number is cumulative, so 英語 would read
// 43 next to an already-selected 廣東話 and be read as "43 English courses".
// The footer button answers that question instead.
function dfFacets(courses, draft, options) {
  const list = Array.isArray(courses) ? courses : [];
  // Rebuilt per call, read synchronously by dfProviderLabel during this same
  // pass. Cheap (one walk of <=500 rows) and avoids threading a second map
  // through the facet machinery for a fallback that is usually unused.
  dfProviderFallback = dfBuildProviderFallback(list);
  const sel = draft || {};
  const withCoverage = !!(options && options.withCoverage);
  const out = {};

  DF_FACETS.forEach(facet => {
    // Pool for THIS facet: everything else she has chosen, applied.
    const others = { ...sel, [facet.key]: [] };
    const pool = dfApply(list, others);

    const counts = new Map();
    const seenRaw = new Map();
    let withValue = 0;
    pool.forEach(c => {
      const values = (facet.read(c) || []).filter(v => v != null && v !== '');
      if (values.length) withValue++;
      values.forEach(v => {
        const k = String(v);
        counts.set(k, (counts.get(k) || 0) + 1);
        if (facet.rawOf && !seenRaw.has(k)) seenRaw.set(k, facet.rawOf(c));
      });
    });

    let opts;
    if (facet.options) {
      opts = facet.options
        .map(o => ({ ...o, count: counts.get(o.value) || 0 }))
        .filter(o => o.count > 0);
    } else {
      opts = [...counts.entries()]
        .map(([value, count]) => ({
          value, count,
          label: facet.labelOf ? facet.labelOf(value, seenRaw.get(value))
            : { en: value, zh: dfZh(value) },
        }))
        .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value));
    }

    if (withCoverage) {
      // Share of the pool that has ANY value for this facet. Selecting a chip
      // silently drops the rest, and she has no way to know how many that is.
      opts.coverage = pool.length ? withValue / pool.length : 1;
      opts.poolSize = pool.length;
    }
    out[facet.key] = opts;
  });
  return out;
}

// Below this, a facet is not describing the catalogue — it is describing the
// minority of it that happens to carry the field, while quietly removing the
// rest from her results. Language sits at 15% on the unfinished pool.
const DF_COVERAGE_FLOOR = 0.4;

function dfApply(courses, f) {
  if (!f) return courses;
  return (courses || []).filter(c => {
    const r = c.raw || {};
    for (const facet of DF_FACETS) {
      const sel = f[facet.key];
      if (!sel || sel.length === 0) continue;
      // Same `read` the counter uses — see dfFacets. OR within a facet, AND
      // across facets. A course with no value for the facet matches nothing,
      // which is why a low-coverage facet has to say so before she picks one.
      const values = (facet.read(c) || []).filter(v => v != null && v !== '');
      if (!values.some(v => sel.includes(String(v)))) return false;
    }
    if (f.points) {
      const p = Number(c.points);
      if (!Number.isFinite(p) || p < f.points[0] || p > f.points[1]) return false;
    }
    // Fee contract (deriveFees, data.js): unknown-fee courses (feeUnknown)
    // match NEITHER Free nor Paid — costFrom is 0 for unknown AND free.
    if (f.cost === 'free' && !c.isFree) return false;
    if (f.cost === 'paid' && !(c.costFrom > 0)) return false;
    // Price band. Same contract as `cost` above: an unknown fee matches
    // NEITHER a band nor Paid, because costFrom is 0 for unknown AND for free.
    // Free courses (explicit $0 evidence) do satisfy a "≤ $N" band — a nurse
    // asking for "under $500" certainly wants the free ones too.
    if (f.priceMax != null) {
      if (c.feeUnknown) return false;
      if (!(c.costFrom <= f.priceMax)) return false;
    }
    if (f.deadlineDays != null) {
      const today = dfToday();
      const d = c.registrationDeadline || r.registration_deadline;
      if (!d) return false;
      const diff = Math.round((new Date(d + 'T00:00:00') - new Date(today + 'T00:00:00')) / 86400000);
      if (diff < 0 || diff > f.deadlineDays) return false;
    }
    return true;
  });
}

function dfCountActive(f) {
  if (!f) return 0;
  let n = 0;
  DF_FACETS.forEach(facet => { if ((f[facet.key] || []).length) n++; });
  if (f.cost) n++;
  if (f.priceMax != null) n++;
  if (f.points) n++;
  if (f.deadlineDays != null) n++;
  if ((f.restrictedMode || 'relevant') !== 'relevant') n++;
  return n;
}

// ─────────────────────────────────────────────────────────────────────────
// <DiscoverFilterSheet> — the sheet itself
// ─────────────────────────────────────────────────────────────────────────
function DiscoverFilterSheet({ open, onClose, filters, onApply, lang, courses, archived, profile, viewTab = 'actionable' }) {
  // Local draft state — we only commit on Apply so users can experiment.
  const [draft, setDraft] = React.useState(() => ({ ...dfEmpty(), ...filters }));
  React.useEffect(() => { if (open) setDraft({ ...dfEmpty(), ...filters }); }, [open]);

  const facetCourses = React.useMemo(() => {
    const base = Array.isArray(courses) ? courses : [];
    const arch = Array.isArray(archived) ? archived : [];
    const seen = new Set(base.map(c => c.id));
    return base.concat(arch.filter(c => !seen.has(c.id)));
  }, [courses, archived]);

  // ‼️ COUNTED ON THE TAB SHE IS LOOKING AT, AND ON HER RESTRICTED SCOPE —
  // the same pipeline the footer count and the result list run. This read
  // `dfFacets(facetCourses)`: every archived course included, no tab, no
  // eligibility scope, no other selection. 「網上 42」 over a list of 8.
  //
  // The precedent is in screens.jsx: hiddenByEligibility computes its number by
  // running the SAME pipeline a second time with one dimension changed and
  // diffing at the SAME tab, precisely so it can never disagree with what she
  // is looking at. This is that rule applied to the chips.
  const countingPool = React.useMemo(() => {
    const mode = draft.restrictedMode || 'relevant';
    let pool = facetCourses;
    if (mode === 'none') pool = pool.filter(c => !c.isRestricted);
    else if (mode === 'relevant') {
      pool = pool.filter(c => !c.isRestricted
        || (window.restrictedVisibleByDefault
            ? window.restrictedVisibleByDefault(c, profile) : true));
    }
    return pool.filter(c => (window.courseBucket?.(c) || 'actionable') === viewTab);
  }, [facetCourses, draft.restrictedMode, profile, viewTab]);

  const facets = React.useMemo(
    () => dfFacets(countingPool, draft, { withCoverage: true }),
    [countingPool, draft]);

  // Mirror the selected Discover tab so the Apply count never includes rows
  // that will immediately be hidden in another date bucket.
  const matchCount = React.useMemo(() => {
    let pool = facetCourses.slice();
    // Tri-state restricted control (mirrors DiscoverScreen.buildFilterBase):
    //   none     — drop all restricted
    //   relevant — drop only the ones she is KNOWN not to qualify for (default)
    //   all      — keep everything
    // ‼️ Must stay in lockstep with buildFilterBase in screens.jsx, or the
    // sheet's Apply count promises a number the list does not deliver. Both
    // call restrictedVisibleByDefault, which is the single predicate.
    const mode = draft.restrictedMode || 'relevant';
    if (mode === 'none') pool = pool.filter(c => !c.isRestricted);
    else if (mode === 'relevant') {
      pool = pool.filter(c => !c.isRestricted
        || (window.restrictedVisibleByDefault
            ? window.restrictedVisibleByDefault(c, profile) : true));
    }
    pool = dfApply(pool, draft);
    return pool.filter(c => (window.courseBucket?.(c) || 'actionable') === viewTab).length;
  }, [facetCourses, draft, profile, viewTab]);

  const activeN = dfCountActive(draft);

  if (!open) return null;

  const toggle = (key, value) => {
    setDraft(d => {
      const cur = d[key] || [];
      const has = cur.includes(value);
      return { ...d, [key]: has ? cur.filter(v => v !== value) : [...cur, value] };
    });
  };

  // Restricted scope commits immediately; other facet choices remain local
  // until the user presses Show results.
  const setScope = (key, value) => {
    setDraft(d => ({ ...d, [key]: value }));
    onApply(f => ({ ...(f || {}), [key]: value }));
  };

  const renderFacet = (facet) => {
    const opts = facets[facet.key] || [];
    if (opts.length === 0) return null;
    const selected = draft[facet.key] || [];

    // ‼️ A LOW-COVERAGE FACET REMOVES MORE THAN IT SELECTS. 15% of unfinished
    // courses publish a language (2026-08-16), so tapping 廣東話 drops the other
    // 85% — not because they are in another language, but because nobody said.
    // She cannot tell those two apart from the chip.
    //
    // The 17 known-Cantonese courses are still genuinely useful to someone who
    // needs Cantonese, so this does NOT hide the facet. It states the coverage,
    // and states it as the count's denominator rather than as a warning icon,
    // because the honest reading is "17 of the 30 we know about" — not "17 of
    // 191". No inference can fix this one: it is ingestion debt, and guessing
    // language from the title would be 0% accurate on this catalogue.
    const coverage = typeof opts.coverage === 'number' ? opts.coverage : null;
    const thin = coverage != null && coverage < DF_COVERAGE_FLOOR;

    // Long lists (provider, event type) get a scrollable region.
    const isLong = opts.length > 12;

    return (
      <div key={facet.key} className="df-section">
        <div className="df-section-head">
          <div className="df-section-title">{L(facet.label, lang)}</div>
          {selected.length > 0 && (
            <button className="df-section-clear" onClick={() => setDraft(d => ({ ...d, [facet.key]: [] }))}>
              {lang === 'zh' ? '清除' : 'Clear'}
            </button>
          )}
        </div>
        {thin && (
          <div className="df-section-note muted-2">
            {lang === 'zh'
              ? `目前只有 ${Math.round(coverage * 100)}% 課程有此資料;選擇後,其餘課程不會顯示。`
              : `Only ${Math.round(coverage * 100)}% of these courses publish this; the rest are hidden once you choose.`}
          </div>
        )}
        <div className={'df-chips' + (isLong ? ' df-chips-scroll' : '')}>
          {opts.map(o => {
            const on = selected.includes(o.value);
            return (
              <button
                key={o.value}
                type="button"
                className={'df-chip' + (on ? ' on' : '')}
                onClick={() => toggle(facet.key, o.value)}
                aria-pressed={on}>
                <span className="df-chip-label">{L(o.label, lang)}</span>
                <span className="df-chip-count">{o.count}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // Portal to <body> so the fixed backdrop escapes the DiscoverScreen
  // subtree. Rendered in-place it stacks INSIDE .page-stack, and the sibling
  // .tab-bar (later in the DOM) paints over the sheet's footer — making the
  // "Show results" / Apply button unreachable (only the instant-commit Status
  // toggles worked). Other app sheets avoid this by mounting at the app root;
  // the portal gives this one the same viewport-level stacking.
  return ReactDOM.createPortal(
    <div className="df-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="df-sheet" role="dialog" aria-modal="true" aria-label={L(STR.filters, lang)}>
        <div className="df-handle" aria-hidden="true" />
        <div className="df-head">
          <div className="df-head-title">{L(STR.filters, lang)}</div>
          <button className="icon-btn" onClick={onClose} aria-label={lang === 'zh' ? '關閉' : 'Close'}>
            <Icon name="close" size={16} />
          </button>
        </div>

        {/* Stagger runs on first mount only — the sheet remounts on each open
            (`if (!open) return null`), and toggling chips re-renders without
            remounting the section nodes, so it never re-cascades on a tap. */}
        <Stagger className="df-body" delay={45} distance={8}>
          {/* Quick row: cost + deadline */}
          <div className="df-section">
            <div className="df-section-head">
              <div className="df-section-title">{lang === 'zh' ? '快速篩選' : 'Quick filters'}</div>
            </div>
            <div className="df-chips">
              <button className={'df-chip' + (draft.cost === 'free' ? ' on' : '')}
                onClick={() => setDraft(d => ({ ...d, cost: d.cost === 'free' ? null : 'free' }))}>
                <span className="df-chip-label">{L(STR.free, lang)}</span>
              </button>
              <button className={'df-chip' + (draft.cost === 'paid' ? ' on' : '')}
                onClick={() => setDraft(d => ({ ...d, cost: d.cost === 'paid' ? null : 'paid' }))}>
                <span className="df-chip-label">{lang === 'zh' ? '付費' : 'Paid'}</span>
              </button>
              {[7, 14, 30].map(n => (
                <button key={n} className={'df-chip' + (draft.deadlineDays === n ? ' on' : '')}
                  onClick={() => setDraft(d => ({ ...d, deadlineDays: d.deadlineDays === n ? null : n }))}>
                  <span className="df-chip-label">
                    {lang === 'zh' ? `${n} 日內截止` : `Deadline ≤ ${n}d`}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Price bands. Fee coverage on live data is ~68% of active courses
              and the paid spread is wide (a third are over $1,000), so a
              ceiling is the single most useful money filter. Unknown-fee rows
              are excluded by dfApply — see the contract note there. */}
          <div className="df-section">
            <div className="df-section-head">
              <div className="df-section-title">{lang === 'zh' ? '價格上限' : 'Price ceiling'}</div>
            </div>
            <div className="df-chips">
              {[300, 500, 1000].map(n => (
                <button key={n} className={'df-chip' + (draft.priceMax === n ? ' on' : '')}
                  onClick={() => setDraft(d => ({ ...d, priceMax: d.priceMax === n ? null : n }))}
                  aria-pressed={draft.priceMax === n}>
                  <span className="df-chip-label">{`≤ $${n.toLocaleString()}`}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Restricted (members-only / internal) courses — tri-state.
              Default 'relevant' hides only what she is KNOWN not to qualify for;
              'all' reveals those too; 'none' hides every restricted course.
              The helper copy below states what 'relevant' actually does now —
              it used to say "shows only courses you may qualify for", which was
              wrong for anyone whose workplace we never asked for: those rows
              were hidden and the sentence implied they had been assessed. */}
          <div className="df-section">
            <div className="df-section-head">
              <div className="df-section-title">{lang === 'zh' ? '受限課程' : 'Restricted courses'}</div>
            </div>
            <div className="df-chips">
              {[
                { id: 'relevant', zh: '相關',   en: 'Relevant' },
                { id: 'all',      zh: '全部',   en: 'All' },
                { id: 'none',     zh: '不顯示', en: 'Hide' },
              ].map(o => {
                const on = (draft.restrictedMode || 'relevant') === o.id;
                return (
                  <button key={o.id}
                    className={'df-chip' + (on ? ' on' : '')}
                    onClick={() => setScope('restrictedMode', o.id)}
                    aria-pressed={on}>
                    <span className="df-chip-label">{lang === 'zh' ? o.zh : o.en}</span>
                  </button>
                );
              })}
            </div>
            <div className="muted-2" style={{ fontSize: 11, lineHeight: 1.45, marginTop: 6 }}>
              {lang === 'zh'
                ? '「相關」會隱藏你確定不符合資格的受限課程。未填寫工作機構時不會隱藏 — 我們不會用沒有的資料替你判斷。'
                : '“Relevant” hides restricted courses you are known not to qualify for. Nothing is hidden while your workplace is blank — we do not judge on data we never asked for.'}
            </div>
          </div>

          {DF_FACETS.map(renderFacet)}

          {/* CNE points range */}
          <div className="df-section">
            <div className="df-section-head">
              <div className="df-section-title">{L(STR.filterPoints, lang)}</div>
              {draft.points && (
                <button className="df-section-clear" onClick={() => setDraft(d => ({ ...d, points: null }))}>
                  {lang === 'zh' ? '清除' : 'Clear'}
                </button>
              )}
            </div>
            <div className="df-chips">
              {[[1,3],[3,6],[6,12],[12,30]].map(([a,b]) => {
                const on = draft.points && draft.points[0] === a && draft.points[1] === b;
                return (
                  <button key={`${a}-${b}`} className={'df-chip' + (on ? ' on' : '')}
                    onClick={() => setDraft(d => ({ ...d, points: on ? null : [a, b] }))}>
                    <span className="df-chip-label">{a}–{b} {L(STR.pts, lang)}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </Stagger>

        <div className="df-foot">
          <button className="btn btn-ghost"
            onClick={() => {
              setDraft(dfEmpty());
              // Scope controls are instant, so clear their committed value too
              // (not just the draft) — otherwise Reset would leave them on.
              onApply(f => ({ ...(f || {}), restrictedMode: 'relevant' }));
            }}
            disabled={activeN === 0}>
            {L(STR.filtersReset, lang)}
          </button>
          <button className="btn btn-primary df-apply" onClick={() => { onApply(draft); onClose(); }}>
            <span>{L(STR.filtersApply, lang)}</span>
            <span className="df-apply-count">{matchCount}</span>
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function dfLabel(key, value, lang) {
  const facet = DF_FACETS.find(item => item.key === key);
  const option = facet?.options?.find(item => item.value === value);
  if (option) return L(option.label, lang);
  return lang === 'zh' ? dfZh(value) : value;
}

window.DiscoverFilters = {
  facets: dfFacets,
  applyFilters: dfApply,
  countActive: dfCountActive,
  emptyFilters: dfEmpty,
  FACETS: DF_FACETS,
  zh: dfZh,
  label: dfLabel,
};
window.DiscoverFilterSheet = DiscoverFilterSheet;

;Object.assign(typeof window !== 'undefined' ? window : globalThis, { DF_LANG_CANON, dfCanonLanguage, dfCanonEventType, dfEventTypeLabel, DF_PROVIDER_UNRESOLVED, dfProviderFallback, dfBuildProviderFallback, dfProviderBucket, dfProviderLabel, DF_FACETS, DF_ZH_SYNONYMS, dfZh, dfToday, dfEmpty, dfFacets, DF_COVERAGE_FLOOR, dfApply, dfCountActive, DiscoverFilterSheet, dfLabel });
